import {createContext, useCallback, useContext, useMemo, useState} from 'react';
import type {ReactNode} from 'react';
import type {LinkedInSyncChatCopyConfig} from '@jobseeker/common-core/constants/linkedinSync';
import type {
  LinkedInMatchPreview,
  LinkedInSyncBranch,
} from '@jobseeker/common-core/services/linkedinSync.service';

export type LinkedInSyncChatBranch = Extract<
  LinkedInSyncBranch,
  'url_exists_unprocessed' | 'no_mismatch' | 'mismatch'
>;

export type LinkedInSyncChatEntryMode = 'edit' | 'save' | 'sync';

export type LinkedInSyncChatTriggerSource =
  | 'post_apply'
  | 'jobfeed'
  | 'onboarding'
  | 'profile_edit';

export interface LinkedInSyncChatOpenOptions {
  chatCopy?: LinkedInSyncChatCopyConfig | null;
  entryMode?: LinkedInSyncChatEntryMode;
  consentEnabled?: boolean;

  linkedinProfileUrl?: string;
  triggerSource?: LinkedInSyncChatTriggerSource;

  syncBranch?: LinkedInSyncChatBranch;
  syncMatch?: LinkedInMatchPreview | null;
}

interface LinkedInSyncChatContextValue {
  open: boolean;
  chatCopy?: LinkedInSyncChatCopyConfig | null;
  entryMode?: LinkedInSyncChatEntryMode | null;
  consentEnabled?: boolean;
  linkedinProfileUrl?: string;
  triggerSource?: LinkedInSyncChatTriggerSource;
  syncBranch?: LinkedInSyncChatBranch;
  syncMatch?: LinkedInMatchPreview | null;

  requestOpen: (options?: LinkedInSyncChatOpenOptions) => void;
  close: () => void;
}

const LinkedInSyncChatContext =
  createContext<LinkedInSyncChatContextValue | null>(null);

export const LinkedInSyncChatProvider = ({children}: {children: ReactNode}) => {
  const [open, setOpen] = useState(false);
  const [chatCopy, setChatCopy] = useState<LinkedInSyncChatCopyConfig | null>(
    null
  );
  const [entryMode, setEntryMode] = useState<LinkedInSyncChatEntryMode | null>(
    null
  );
  const [consentEnabled, setConsentEnabled] = useState(true);
  const [linkedinProfileUrl, setLinkedinProfileUrl] = useState('');
  const [triggerSource, setTriggerSource] =
    useState<LinkedInSyncChatTriggerSource>('post_apply');
  const [syncBranch, setSyncBranch] = useState<
    LinkedInSyncChatBranch | undefined
  >(undefined);
  const [syncMatch, setSyncMatch] = useState<LinkedInMatchPreview | null>(null);

  const requestOpen = useCallback((options?: LinkedInSyncChatOpenOptions) => {
    setChatCopy(options?.chatCopy ?? null);
    setEntryMode(options?.entryMode ?? null);
    setConsentEnabled(options?.consentEnabled ?? true);
    setLinkedinProfileUrl(options?.linkedinProfileUrl?.trim() ?? '');
    setTriggerSource(options?.triggerSource ?? 'post_apply');
    setSyncBranch(options?.syncBranch);
    setSyncMatch(options?.syncMatch ?? null);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  const value = useMemo<LinkedInSyncChatContextValue>(
    () => ({
      open,
      chatCopy,
      entryMode,
      consentEnabled,
      linkedinProfileUrl,
      triggerSource,
      syncBranch,
      syncMatch,
      requestOpen,
      close,
    }),
    [
      open,
      chatCopy,
      entryMode,
      consentEnabled,
      linkedinProfileUrl,
      triggerSource,
      syncBranch,
      syncMatch,
      requestOpen,
      close,
    ]
  );

  return (
    <LinkedInSyncChatContext.Provider value={value}>
      {children}
    </LinkedInSyncChatContext.Provider>
  );
};

export const useLinkedInSyncChat = (): LinkedInSyncChatContextValue => {
  const ctx = useContext(LinkedInSyncChatContext);
  if (ctx) return ctx;

  return {
    open: false,
    chatCopy: null,
    entryMode: null,
    consentEnabled: true,
    linkedinProfileUrl: '',
    triggerSource: 'post_apply',
    syncBranch: undefined,
    syncMatch: null,
    requestOpen: () => undefined,
    close: () => undefined,
  };
};

export default LinkedInSyncChatContext;
